import React, { useState, useEffect, useCallback } from 'react';
import {
  Lock, RefreshCw, Eye, AlertCircle, X, ChevronLeft, ChevronRight,
  CheckCircle, Image as ImageIcon, Layers
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import BBoxAnnotator from '@/components/BBoxAnnotationDialog';
import ConfigSidebar from '@/components/reviews/ConfigSidebar';
import VariantCarousel from '@/components/reviews/VariantCarousel';
import ReadinessBar from '@/components/reviews/ReadinessBar';
import config from '@/config';

// Review is per FRAME (upc), not per image. One upc is one glasses frame and
// its nodes are the lens-colour variants; the backend already applies a verdict
// to every variant, so the UI groups them and decides them together:
//   1. step through every variant (the carousel gates the decision)
//   2. annotate the representative image - the oldest variant
//   3. confirm once; the verdict covers the whole frame

function GroupReviewModal({
  isOpen,
  onClose,
  group,
  lockId,
  configData,
  userInfo,
  totalGroups,
  groupIndex,
  onCompleted,
}) {
  const { authenticatedFetch } = useAuth();
  const [phase, setPhase] = useState('review');   // 'review' | 'decide'
  const [allSeen, setAllSeen] = useState(false);
  const [hasAnnotations, setHasAnnotations] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [successAction, setSuccessAction] = useState(null);
  const [bboxKey, setBboxKey] = useState(0);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const variants = group?.variants || [];
  const representative = variants[0] || null;

  // Reset whenever a different frame is opened.
  useEffect(() => {
    if (isOpen) {
      setPhase('review');
      setAllSeen(variants.length <= 1);
      setHasAnnotations(false);
      setSuccessAction(null);
      setBboxKey((k) => k + 1);
    }
  }, [isOpen, group?.upc]); // eslint-disable-line react-hooks/exhaustive-deps

  // The annotator binds keys; don't let typing in its comment box trigger them.
  useEffect(() => {
    const onFocusIn = (e) => {
      const t = e.target?.tagName;
      if (t === 'INPUT' || t === 'TEXTAREA') setIsInputFocused(true);
    };
    const onFocusOut = () => setTimeout(() => setIsInputFocused(false), 0);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    return () => {
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
    };
  }, []);

  const handleCloseClick = () => {
    if (hasAnnotations) {
      setShowCloseConfirm(true);
    } else {
      onClose();
    }
  };

  // Completing the representative node propagates the verdict to the rest of
  // the frame group on the backend.
  const handleComplete = async (result) => {
    if (!representative) return;
    setIsCompleting(true);

    try {
      const payload = {
        node_id: representative.node_id,
        status: result.status,
        labels_data: result.labels_data,
        comment: result.comment || null,
        apply_to_frame_group: true,
      };

      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/nodes/final-verification/complete`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to complete review');
      }

      const data = await response.json();
      setSuccessAction({
        status: result.status,
        // +1 for the representative itself.
        count: (data.propagated_count || 0) + 1,
        skipped: (data.skipped_locked_node_ids || []).length,
      });

      await new Promise((r) => setTimeout(r, 1200));
      setSuccessAction(null);
      onCompleted();
    } catch (err) {
      console.error('Error completing group review:', err);
      alert(`Failed to complete review: ${err.message}`);
    } finally {
      setIsCompleting(false);
    }
  };

  if (!isOpen || !group) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full h-full max-w-[98vw] max-h-[98vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-4 py-3 flex-shrink-0">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <Layers className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                <span className="font-mono text-sm font-semibold text-gray-900 truncate">
                  {group.upc}
                </span>
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                {variants.length} {variants.length === 1 ? 'variant' : 'variants'} in this frame
                {typeof groupIndex === 'number' && totalGroups ? (
                  <> &middot; frame {groupIndex + 1} of {totalGroups}</>
                ) : null}
              </div>
            </div>

            {/* Phase indicator */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className={`px-3 py-1.5 rounded-md text-xs font-medium ${
                phase === 'review' ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-500'
              }`}>
                1. Review all variants
              </div>
              <ChevronRight className="w-4 h-4 text-gray-300" />
              <div className={`px-3 py-1.5 rounded-md text-xs font-medium ${
                phase === 'decide' ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-500'
              }`}>
                2. Mark &amp; decide
              </div>
            </div>

            <div className="flex items-center gap-3 flex-shrink-0">
              {userInfo && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-indigo-50 rounded-md border border-indigo-200">
                  <span className="text-sm font-medium text-indigo-900">
                    {userInfo.first_name || 'User'}
                  </span>
                  <span className="text-indigo-300">•</span>
                  {userInfo.is_unlimited ? (
                    <span className="text-xs text-indigo-700 font-medium">Unlimited</span>
                  ) : (
                    <span className="text-xs text-indigo-700">
                      <span className="font-semibold">{userInfo.reviews_remaining}</span> left
                    </span>
                  )}
                </div>
              )}
              <button
                onClick={handleCloseClick}
                disabled={isCompleting}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 flex overflow-hidden">
          <ConfigSidebar configData={configData} />

          {phase === 'review' ? (
            <div className="flex-1 flex flex-col overflow-hidden">
              <VariantCarousel variants={variants} onAllSeen={() => setAllSeen(true)} />

              <div className="border-t border-gray-200 bg-white px-4 py-3 flex items-center justify-between flex-shrink-0">
                <div className="text-sm">
                  {allSeen ? (
                    <span className="text-green-700 font-medium flex items-center gap-1.5">
                      <CheckCircle className="w-4 h-4" />
                      All {variants.length} {variants.length === 1 ? 'variant' : 'variants'} reviewed
                    </span>
                  ) : (
                    <span className="text-gray-600">
                      Step through every variant to continue
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setPhase('decide')}
                  disabled={!allSeen}
                  className="px-5 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  Continue to decision
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 flex items-center justify-between flex-shrink-0">
                <div className="text-xs text-amber-900">
                  Marking <span className="font-semibold">{representative?.lens_color || 'representative'}</span>
                  {' '}&mdash; your verdict applies to all {variants.length}{' '}
                  {variants.length === 1 ? 'variant' : 'variants'} of this frame.
                </div>
                <button
                  onClick={() => setPhase('review')}
                  disabled={isCompleting}
                  className="text-xs text-amber-900 underline hover:no-underline flex items-center gap-1 disabled:opacity-50"
                >
                  <ChevronLeft className="w-3 h-3" />
                  Back to variants
                </button>
              </div>

              <div className="flex-1 overflow-hidden relative bg-white">
                <BBoxAnnotator
                  key={bboxKey}
                  imageUrl={representative?.image_url}
                  tags={[
                    { tag: 'lens masking error', color: '#ef4444' },
                    { tag: 'logo error', color: '#f59e0b' },
                    { tag: 'background error', color: '#3b82f6' },
                    { tag: 'lens removal error', color: '#3bf64eff' },
                  ]}
                  review_lock_id={lockId}
                  review_id={representative?.node_id}
                  onComplete={handleComplete}
                  onAnnotationsChange={setHasAnnotations}
                  containerMode={true}
                  disableSpaceDrag={isInputFocused}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {showCloseConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="w-6 h-6 text-orange-600" />
              <h3 className="text-lg font-semibold">Unsaved Changes</h3>
            </div>
            <p className="text-gray-600 mb-6">
              You have unsaved annotations on this frame. Close anyway?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowCloseConfirm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {successAction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl p-8 flex flex-col items-center">
            <CheckCircle className={`w-16 h-16 mb-4 ${
              successAction.status === 'accepted' ? 'text-green-600' : 'text-red-600'
            }`} />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Frame {successAction.status === 'accepted' ? 'accepted' : 'rejected'}
            </h3>
            <p className="text-gray-600">
              Applied to {successAction.count}{' '}
              {successAction.count === 1 ? 'variant' : 'variants'}
              {successAction.skipped > 0 && (
                <span className="text-amber-700">
                  {' '}&middot; {successAction.skipped} skipped (in use)
                </span>
              )}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}


function ReviewManagerFinal() {
  const { authenticatedFetch } = useAuth();

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [hasReachedLimit, setHasReachedLimit] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [readiness, setReadiness] = useState(null);

  const [lockingUpc, setLockingUpc] = useState(null);
  const [currentLockId, setCurrentLockId] = useState(null);
  const [currentConfigData, setCurrentConfigData] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);

  const fetchUserInfo = useCallback(async () => {
    try {
      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/nodes/final-verification/info/user`
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setUserInfo(data);
      setHasReachedLimit(
        !data.is_unlimited && data.reviews_remaining !== null && data.reviews_remaining <= 0
      );
    } catch (err) {
      console.error('Error fetching user info:', err);
    }
  }, [authenticatedFetch]);

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: currentPage, page_size: pageSize });
      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/nodes/final-verification/groups?${params.toString()}`
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      setGroups(data.items || []);
      setReadiness(data.readiness || null);
      setTotalPages(data.total_pages || 0);
      setTotalCount(data.total || 0);
    } catch (err) {
      console.error('Error fetching frame groups:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch, currentPage, pageSize]);

  const handleRefresh = useCallback(async () => {
    await Promise.all([fetchGroups(), fetchUserInfo()]);
  }, [fetchGroups, fetchUserInfo]);

  useEffect(() => {
    handleRefresh();
  }, [handleRefresh]);

  const handleStartVerification = async (index) => {
    const group = groups[index];
    if (!group || hasReachedLimit) return;

    setLockingUpc(group.upc);
    try {
      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/nodes/final-verification/group/lock`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ upc: group.upc }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to lock frame');
      }

      const data = await response.json();
      setCurrentLockId(data.lock_id);
      setCurrentConfigData({
        needs_logo: data.needs_logo,
        logo_data: data.logo_data,
        lens_data: data.lens_data,
      });
      setSelectedIndex(index);
      setModalOpen(true);
    } catch (err) {
      console.error('Error locking frame group:', err);
      alert(`Failed to start verification: ${err.message}`);
    } finally {
      setLockingUpc(null);
    }
  };

  const releaseGroup = async (upc) => {
    if (!upc) return;
    try {
      await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/nodes/final-verification/group/release`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ upc }),
        }
      );
    } catch (err) {
      console.error('Error releasing frame group:', err);
    }
  };

  const closeModal = async ({ release }) => {
    const group = selectedIndex !== null ? groups[selectedIndex] : null;
    if (release && group) {
      await releaseGroup(group.upc);
    }
    setModalOpen(false);
    setCurrentLockId(null);
    setCurrentConfigData(null);
    setSelectedIndex(null);
    await handleRefresh();
  };

  const selectedGroup = selectedIndex !== null ? groups[selectedIndex] : null;

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Final Human Review</h1>
          <p className="text-sm text-gray-600 mt-1">
            Grouped by frame (UPC). Reviewing a frame decides all of its lens-colour variants at once.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {userInfo && (
            <div className="flex items-center gap-2 px-3 py-2 bg-indigo-50 rounded-md border border-indigo-200">
              <span className="text-sm font-medium text-indigo-900">
                {userInfo.first_name || 'User'}
              </span>
              <span className="text-indigo-300">•</span>
              {userInfo.is_unlimited ? (
                <span className="text-xs text-indigo-700 font-medium">Unlimited</span>
              ) : (
                <span className="text-xs text-indigo-700">
                  <span className="font-semibold">{userInfo.reviews_remaining}</span> remaining
                </span>
              )}
            </div>
          )}
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {hasReachedLimit && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-md flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            You have reached your verification limit. Contact an administrator to continue.
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-900">{error}</div>
        </div>
      )}

      <ReadinessBar readiness={readiness} unitLabel="images" />

      <div className="mb-3 text-sm text-gray-600">
        {totalCount} {totalCount === 1 ? 'frame' : 'frames'} awaiting review
      </div>

      {loading && groups.length === 0 ? (
        <div className="py-20 flex flex-col items-center gap-3 text-gray-500">
          <RefreshCw className="w-8 h-8 animate-spin" />
          <span className="text-sm">Loading frames…</span>
        </div>
      ) : groups.length === 0 ? (
        <div className="py-20 flex flex-col items-center gap-3 text-gray-500">
          <CheckCircle className="w-10 h-10 text-green-500" />
          <span className="text-sm">Nothing left to review.</span>
        </div>
      ) : (
        <div className="space-y-3">
          {groups.map((group, index) => (
            <div
              key={group.upc}
              className="border border-gray-200 rounded-lg bg-white p-4 hover:border-indigo-300 transition-colors"
            >
              <div className="flex items-center gap-4">
                {/* Variant previews */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {group.variants.slice(0, 5).map((v) => (
                    <div
                      key={v.node_id}
                      className="w-20 h-20 rounded border border-gray-200 overflow-hidden bg-gray-50 flex items-center justify-center"
                      title={v.lens_color || ''}
                    >
                      {v.image_url ? (
                        <img
                          src={v.image_url}
                          alt={v.lens_color || ''}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <ImageIcon className="w-6 h-6 text-gray-400" />
                      )}
                    </div>
                  ))}
                  {group.variant_count > 5 && (
                    <div className="w-20 h-20 rounded border border-dashed border-gray-300 flex items-center justify-center text-sm text-gray-500 font-medium">
                      +{group.variant_count - 5}
                    </div>
                  )}
                </div>

                {/* Frame details */}
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-sm font-semibold text-gray-900 truncate">
                    {group.upc}
                  </div>
                  <div className="mt-1 flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-xs font-medium">
                      <Layers className="w-3 h-3" />
                      {group.variant_count} {group.variant_count === 1 ? 'variant' : 'variants'}
                    </span>
                    {group.variants.slice(0, 4).map((v) => (
                      <span
                        key={v.node_id}
                        className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs"
                      >
                        {v.lens_color || 'unknown'}
                      </span>
                    ))}
                    {group.variant_count > 4 && (
                      <span className="text-xs text-gray-400">
                        +{group.variant_count - 4} more
                      </span>
                    )}
                  </div>
                </div>

                <button
                  onClick={() => handleStartVerification(index)}
                  disabled={hasReachedLimit || lockingUpc === group.upc}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 flex-shrink-0"
                >
                  {lockingUpc === group.upc ? (
                    <>
                      <Lock className="w-4 h-4 animate-pulse" />
                      Locking…
                    </>
                  ) : (
                    <>
                      <Eye className="w-4 h-4" />
                      Start verification
                    </>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1 || loading}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40 flex items-center gap-1"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </button>
          <span className="text-sm text-gray-600">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage >= totalPages || loading}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40 flex items-center gap-1"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      <GroupReviewModal
        isOpen={modalOpen}
        group={selectedGroup}
        lockId={currentLockId}
        configData={currentConfigData}
        userInfo={userInfo}
        totalGroups={totalCount}
        groupIndex={selectedIndex}
        onClose={() => closeModal({ release: true })}
        onCompleted={() => closeModal({ release: false })}
      />
    </div>
  );
}

export default ReviewManagerFinal;
